import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CLIENT_FIELDS } from "@/lib/config";
import RecordDialog from "@/components/RecordDialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { Search, Save, Plus } from "lucide-react";

const INTEREST_COLORS = { Hot: "bg-seared/10 text-seared border-seared/30", Warm: "bg-seaamber/10 text-seaamber border-seaamber/30", Cold: "bg-sky/10 text-atlantic border-sky/30", Nurture: "bg-cloud text-slate border-border", Unknown: "bg-cloud text-mutedsea border-border" };

export default function Prospects() {
  const { level } = useAuth();
  const nav = useNavigate();
  const isAdmin = level >= 80;
  const [meta, setMeta] = useState({ quick_lists: [], interest_levels: [], xdate_buckets: [] });
  const [producers, setProducers] = useState([]);
  const [views, setViews] = useState([]);
  const [f, setF] = useState({ q: "", producer: "", interest: "", state: "", industry: "", xdate: "", quick: "all", has_submissions: "" });
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    api.get("/prospects/quick-lists").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/prospects/saved-lists").then(({ data }) => setViews(data.items)).catch(() => {});
    if (isAdmin) api.get("/dashboard/agency").then(({ data }) => setProducers(data.producers || [])).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.set(k === "has_submissions" ? "has_submissions" : k, v); });
    if (f.quick === "with_submissions") p.set("has_submissions", "true");
    api.get(`/prospects/search?${p.toString()}`).then(({ data }) => setData(data)).catch(() => toast.error("Search failed")).finally(() => setLoading(false));
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const saveList = async () => {
    const name = window.prompt("Save this prospect list as:");
    if (!name) return;
    try { const { data } = await api.post("/prospects/saved-lists", { name, criteria: f }); setViews([data, ...views]); toast.success("Saved."); }
    catch { toast.error("Save failed."); }
  };

  return (
    <div className="p-6 animate-fade-in" data-testid="prospects-page">
      <PageHeader title="Prospects" subtitle="Dedicated prospect workspace — search, quick lists, submissions and follow-ups, scoped to your book.">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={saveList} data-testid="prospect-save-list" className="border-atlantic text-atlantic"><Save className="h-3.5 w-3.5 mr-1" /> Save List</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" data-testid="new-prospect" className="bg-navy hover:bg-atlantic text-white"><Plus className="h-3.5 w-3.5 mr-1" /> New Prospect</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem data-testid="new-prospect-person" onClick={() => setDialog({ type: "person" })}>Individual (Person)</DropdownMenuItem>
              <DropdownMenuItem data-testid="new-prospect-organization" onClick={() => setDialog({ type: "organization" })}>Business (Organization)</DropdownMenuItem>
              <DropdownMenuItem data-testid="new-prospect-household" onClick={() => setDialog({ type: "household" })}>Household</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-mutedsea" />
          <Input data-testid="prospect-search" placeholder="Prospect, company, DBA, email, phone, ID…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="h-9 w-72 pl-8" />
        </div>
        <Select value={f.quick} onValueChange={(v) => setF({ ...f, quick: v })}>
          <SelectTrigger className="w-48 h-9" data-testid="prospect-quicklist"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{meta.quick_lists.map((q) => <SelectItem key={q.key} value={q.key}>{q.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.interest || "any"} onValueChange={(v) => setF({ ...f, interest: v === "any" ? "" : v })}>
          <SelectTrigger className="w-36 h-9" data-testid="prospect-interest"><SelectValue placeholder="Interest" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="any">Any Interest</SelectItem>{meta.interest_levels.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.xdate || "any"} onValueChange={(v) => setF({ ...f, xdate: v === "any" ? "" : v })}>
          <SelectTrigger className="w-40 h-9" data-testid="prospect-xdate"><SelectValue placeholder="X-Date" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="any">Any X-Date</SelectItem>{meta.xdate_buckets.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="prospect-state" placeholder="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="h-9 w-24" />
        <Input data-testid="prospect-industry" placeholder="Industry" value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} className="h-9 w-32" />
        {isAdmin && (
          <Select value={f.producer || "all"} onValueChange={(v) => setF({ ...f, producer: v === "all" ? "" : v })}>
            <SelectTrigger className="w-44 h-9" data-testid="prospect-producer"><SelectValue placeholder="All Producers" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72"><SelectItem value="all">All Producers</SelectItem>{producers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {views.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="prospect-saved-lists">
          {views.map((v) => <button key={v.id} onClick={() => setF({ ...f, ...v.criteria })} className="text-xs px-2 py-1 border border-sky/40 bg-ice text-atlantic rounded-sm nav-link" data-testid={`prospect-view-${v.id}`}>{v.name}</button>)}
        </div>
      )}

      <div className="text-xs text-mutedsea mb-2" data-testid="prospect-total">{data.total} prospect(s){loading ? " · loading…" : ""}</div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{["Prospect", "Interest", "Lead Source", "X-Date", "Next Follow-Up", "Opps", "Subs", "Producer", "Actions"].map((h) => <th key={h} className="text-left px-3 py-2 text-[11px] uppercase text-navy font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {data.items.length === 0 ? <tr><td colSpan={9} className="px-3 py-6 text-center text-mutedsea">No prospects for the current scope/filters.</td></tr> :
              data.items.map((p) => (
                <tr key={p.id} data-testid={`prospect-${p.id}`} className="border-b border-border last:border-0 hover:bg-ice">
                  <td className="px-3 py-2"><div className="text-slate font-medium">{p.name}</div><div className="text-xs text-mutedsea">{p.company || p.email || p.phone || "—"}</div>{p.existing_client && <Badge variant="outline" data-testid={`existing-client-${p.id}`} className="mt-1 bg-seagreen/10 text-seagreen border-seagreen/30 text-[10px] uppercase tracking-wide">Existing Client: Yes</Badge>}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={INTEREST_COLORS[p.interest] || INTEREST_COLORS.Unknown}>{p.interest}</Badge></td>
                  <td className="px-3 py-2 text-xs text-mutedsea">{p.lead_source}</td>
                  <td className="px-3 py-2 text-xs text-mutedsea">{p.x_date || "—"}</td>
                  <td className="px-3 py-2 text-xs text-mutedsea">{p.next_follow_up || "—"}</td>
                  <td className="px-3 py-2 text-center">{p.opportunities}</td>
                  <td className="px-3 py-2 text-center">{p.submissions}</td>
                  <td className="px-3 py-2 text-xs text-mutedsea whitespace-nowrap">{p.producer}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`open-${p.id}`} onClick={() => nav(`/clients/${p.id}`)} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Open</button>
                      <button data-testid={`opps-${p.id}`} onClick={() => nav("/opportunities")} className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-cloud">Opportunities</button>
                      <button data-testid={`convert-${p.id}`} onClick={() => nav(`/clients/${p.id}`)} className="text-xs px-2 py-1 border border-seagreen/40 text-seagreen rounded-sm hover:bg-seagreen/5">Convert</button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {dialog && (
        <RecordDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}
          fields={CLIENT_FIELDS[dialog.type]} title="New Prospect"
          initial={{ party_type: dialog.type, relationship: "Prospect", status: "Active" }}
          endpoint="/parties" method="post"
          onSaved={(rec) => { setDialog(null); if (rec?.id) nav(`/clients/${rec.id}`); else load(); }} />
      )}
    </div>
  );
}
